import React, { useState, useEffect } from "react";
import {
  Space,
  Select,
  Typography,
  Spin,
  Card,
  Descriptions,
  Tag,
  Divider,
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { usePermissions } from "../../../../PermissionsProvider";
import { DatasetService } from "../../../../services/DatasetService";
import { FinetuneData } from "../NewFinetuneRun";
import { FinetuneDataset } from "../../../../model_gen/finetuning";

const { Title, Text } = Typography;

interface Dataset {
  id: number;
  name: string;
  description?: string;
  created_at?: string;
  intent_group_name?: string;
  application_name?: string;
  intent_name?: string;
  request_count?: number;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  company_id?: number;
}

interface ChooseDatasetStepProps {
  selectedDataset: string;
  setSelectedDataset: (value: string) => void;
  isCreatingFinetune: boolean;
  setFinetuneData: (updater: (prev: FinetuneData) => FinetuneData) => void;
}

const ChooseDatasetStep: React.FC<ChooseDatasetStepProps> = ({
  selectedDataset,
  setSelectedDataset,
  isCreatingFinetune,
  setFinetuneData,
}) => {
  const { apiKey } = usePermissions();
  const [isLoadingDatasets, setIsLoadingDatasets] = useState<boolean>(false);
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [selectedDatasetInfo, setSelectedDatasetInfo] =
    useState<Dataset | null>(null);
  const [subDatasets, setSubDatasets] = useState<FinetuneDataset[]>([]);
  const [selectedSubDataset, setSelectedSubDataset] =
    useState<FinetuneDataset | null>(null);

  // Fetch datasets on component mount
  useEffect(() => {
    // Using offset=0 and limit=100 to get a reasonable number of datasets
    DatasetService.getDatasets(0, 100, setIsLoadingDatasets, apiKey).subscribe(
      (data: any) => {
        if (data && Array.isArray(data.items)) {
          setDatasets(data.items);
        } else {
          console.error("Unexpected dataset response format:", data);
          setDatasets([]);
        }
      },
      (error: Error) => {
        console.error("Error fetching datasets:", error);
      }
    );
  }, [apiKey]);

  // Update selected dataset info when selectedDataset changes or datasets are loaded
  useEffect(() => {
    if (selectedDataset && datasets.length > 0) {
      const dataset = datasets.find((d) => d.id.toString() === selectedDataset);
      setSelectedDatasetInfo(dataset || null);

      // Reset sub-dataset selection when dataset changes
      setSelectedSubDataset(null);

      // Filter the existing datasets list to find potential sub-datasets
      if (dataset) {
        // Filter datasets to exclude the currently selected dataset
        const filteredSubDatasets = datasets.filter((d) => d.id !== dataset.id);
        setSubDatasets(filteredSubDatasets as FinetuneDataset[]);
      } else {
        setSubDatasets([]);
      }
    } else {
      setSelectedDatasetInfo(null);
      setSubDatasets([]);
      setSelectedSubDataset(null);
    }
  }, [selectedDataset, datasets]);

  // Helper function to get the source name for a dataset
  const getSourceName = (dataset: Dataset): string => {
    if (dataset.intent_group_name) {
      return dataset.intent_group_name;
    } else if (dataset.application_name && dataset.intent_name) {
      return `${dataset.application_name} / ${dataset.intent_name}`;
    } else {
      return "";
    }
  };

  // Helper function to get the source type label
  const getSourceTypeLabel = (dataset: Dataset): string => {
    if (dataset.intent_group_name) {
      return "Intent Group";
    } else if (dataset.application_name && dataset.intent_name) {
      return "Application/Intent";
    } else {
      return "Unknown";
    }
  };

  // Handle dataset selection
  const handleDatasetChange = (value: string) => {
    setSelectedDataset(value);

    // Find the selected dataset to get its IDs
    const selectedDataset = datasets.find((d) => d.id.toString() === value);

    if (selectedDataset) {
      // Update the finetune data with all relevant IDs
      setFinetuneData((prev) => ({
        ...prev,
        dataset_id: selectedDataset.id,
        application_id: selectedDataset.application_id,
        intent_id: selectedDataset.intent_id,
        intent_group_id: selectedDataset.intent_group_id,
        selected_sub_dataset: null, // Reset sub-dataset when dataset changes
      }));
    }
  };

  // Handle sub-dataset selection
  const handleSubDatasetChange = (value: string) => {
    const subDataset = subDatasets.find((d) => d.id.toString() === value);
    setSelectedSubDataset(subDataset || null);

    // Update the finetune data with the selected sub-dataset
    setFinetuneData((prev) => ({
      ...prev,
      selected_sub_dataset: subDataset || null,
    }));
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Title level={4}>Choose Dataset</Title>

      {isLoadingDatasets ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
          <p style={{ marginTop: "16px" }}>Loading datasets...</p>
        </div>
      ) : (
        <div>
          <h4>Dataset</h4>
          <Select
            style={{ width: "100%" }}
            placeholder="Select a dataset"
            value={selectedDataset}
            onChange={handleDatasetChange}
            disabled={isCreatingFinetune}
            optionFilterProp="children"
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                .toLowerCase()
                .includes(input.toLowerCase())
            }>
            {datasets.map((dataset) => {
              const sourceName = getSourceName(dataset);
              const displayName = sourceName
                ? `${dataset.name} - ${sourceName}`
                : dataset.name;

              return (
                <Select.Option
                  key={dataset.id.toString()}
                  value={dataset.id.toString()}>
                  {displayName}
                </Select.Option>
              );
            })}
          </Select>

          {selectedDatasetInfo && (
            <Card
              style={{
                marginTop: "16px",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
                borderRadius: "8px",
              }}
              bordered={true}>
              <Descriptions
                title={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}>
                    <FileTextOutlined
                      style={{
                        fontSize: "16px",
                        marginRight: "8px",
                        color: "#21b892",
                      }}
                    />
                    <span>Dataset Details</span>
                  </div>
                }
                column={1}
                size="small"
                labelStyle={{ width: "150px", fontWeight: "500" }}
                contentStyle={{ backgroundColor: "#fff" }}>
                <Descriptions.Item label="Data Source">
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}>
                    <Space>
                      <Tag color="#21b892">
                        {getSourceTypeLabel(selectedDatasetInfo)}
                      </Tag>
                    </Space>
                    {selectedDatasetInfo.intent_group_name && (
                      <Text>{selectedDatasetInfo.intent_group_name}</Text>
                    )}
                    {selectedDatasetInfo.application_name && (
                      <Text>
                        Application: {selectedDatasetInfo.application_name}
                      </Text>
                    )}
                    {selectedDatasetInfo.intent_name && (
                      <Text>Intent: {selectedDatasetInfo.intent_name}</Text>
                    )}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Request Count">
                  <Text style={{ color: "#21b892", fontWeight: "500" }}>
                    {selectedDatasetInfo.request_count || 0} requests
                  </Text>
                </Descriptions.Item>

                {selectedDatasetInfo.description && (
                  <Descriptions.Item label="Description">
                    {selectedDatasetInfo.description}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}

          {/* Sub-dataset selection section - only shown for intent group datasets */}
          {selectedDatasetInfo && (
            <>
              <Divider style={{ margin: "24px 0 16px" }} />
              <h4>Target Dataset Selection</h4>

              <div style={{ marginTop: "16px" }}>
                {subDatasets.length > 0 ? (
                  <>
                    <Text style={{ display: "block", marginBottom: "8px" }}>
                      Optionally select a target dataset to fine-tune
                      specifically for:
                    </Text>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Select a target dataset"
                      value={selectedSubDataset?.id.toString()}
                      onChange={handleSubDatasetChange}
                      disabled={isCreatingFinetune}
                      optionFilterProp="children"
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }>
                      {subDatasets.map((subDataset) => (
                        <Select.Option
                          key={subDataset.id.toString()}
                          value={subDataset.id.toString()}>
                          {subDataset.name} ({subDataset.request_count || 0}{" "}
                          requests)
                        </Select.Option>
                      ))}
                    </Select>
                  </>
                ) : (
                  <Text type="secondary">No target datasets available</Text>
                )}
              </div>
            </>
          )}

          {datasets.length === 0 && !isLoadingDatasets && (
            <p style={{ color: "red", marginTop: "8px" }}>
              No datasets found. Please create a dataset first.
            </p>
          )}
        </div>
      )}
    </Space>
  );
};

export default ChooseDatasetStep;
